import { useState, useEffect } from "react";
import { createPortal } from "react-dom";
import { useTranslation } from "react-i18next";

// "?" in the header → short how-to pages (EN + ES, from the "help"
// locale namespace). Full screen on a phone, a centered panel on desktop.
const cyan = "#00f5ff";
const border = "rgba(0,245,255,0.15)";

export const HELP_TOPICS = ["gettingStarted", "rigReport", "rigFieldReport", "pumper", "drillout", "fracStages", "buildSheet", "safety", "sms"];

function useIsMobile() {
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
  useEffect(() => {
    const handler = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener("resize", handler);
    return () => window.removeEventListener("resize", handler);
  }, []);
  return isMobile;
}

export function HelpArticle({ topic }) {
  const { t } = useTranslation();
  const sections = t(`help.topics.${topic}.sections`, { returnObjects: true });
  return (
    <article data-testid={`help-article-${topic}`}>
      <h2 style={{ color: "white", fontSize: "20px", margin: "0 0 6px" }}>{t(`help.topics.${topic}.title`)}</h2>
      <p style={{ color: "rgba(255,255,255,0.7)", fontSize: "15px", lineHeight: 1.6, margin: "0 0 16px" }}>
        {t(`help.topics.${topic}.intro`)}
      </p>
      {(Array.isArray(sections) ? sections : []).map((s) => (
        <section key={s.heading} style={{ marginBottom: "18px" }}>
          <h3 style={{ color: cyan, fontSize: "14px", letterSpacing: "1px", margin: "0 0 8px" }}>{s.heading}</h3>
          {Array.isArray(s.steps) && (
            <ol style={{ color: "rgba(255,255,255,0.85)", fontSize: "15px", lineHeight: 1.6, paddingLeft: "22px", margin: 0 }}>
              {s.steps.map((step) => (
                <li key={step} style={{ marginBottom: "6px" }}>
                  {step}
                </li>
              ))}
            </ol>
          )}
          {s.tip && (
            <p
              style={{
                color: "#ffcc66",
                fontSize: "14px",
                lineHeight: 1.5,
                margin: "8px 0 0",
                padding: "8px 10px",
                border: "1px solid rgba(255,170,0,0.3)",
                borderRadius: "6px",
                backgroundColor: "rgba(255,170,0,0.06)",
              }}
            >
              {s.tip}
            </p>
          )}
        </section>
      ))}
    </article>
  );
}

export default function HelpButton({ buttonStyle }) {
  const { t } = useTranslation();
  const isMobile = useIsMobile();
  const [open, setOpen] = useState(false);
  const [topic, setTopic] = useState(null);

  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => {
      if (e.key === "Escape") setOpen(false);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open]);

  const close = () => {
    setOpen(false);
    setTopic(null);
  };

  const navBtn = {
    minHeight: "44px",
    padding: "0 14px",
    background: "none",
    border: `1px solid ${border}`,
    borderRadius: "6px",
    color: cyan,
    fontSize: "13px",
    fontWeight: "700",
    cursor: "pointer",
  };

  return (
    <>
      <button
        type="button"
        onClick={() => setOpen(true)}
        aria-label={t("help.open")}
        title={t("help.open")}
        data-testid="help-button"
        style={{
          minWidth: "36px",
          minHeight: "36px",
          borderRadius: "50%",
          border: `1px solid ${cyan}`,
          background: "rgba(0,245,255,0.08)",
          color: cyan,
          fontWeight: "800",
          fontSize: "15px",
          cursor: "pointer",
          lineHeight: 1,
          ...buttonStyle,
        }}
      >
        ?
      </button>
      {open &&
        createPortal(
          <div
            role="dialog"
            aria-modal="true"
            aria-label={t("help.title")}
            onClick={close}
            style={{
              position: "fixed",
              inset: 0,
              zIndex: 2000,
              backgroundColor: "rgba(0,0,0,0.75)",
              display: "flex",
              alignItems: isMobile ? "stretch" : "center",
              justifyContent: "center",
            }}
          >
            <div
              onClick={(e) => e.stopPropagation()}
              style={{
                backgroundColor: "#020817",
                border: isMobile ? "none" : `1px solid ${border}`,
                borderRadius: isMobile ? 0 : "12px",
                width: isMobile ? "100%" : "620px",
                maxWidth: "100%",
                maxHeight: isMobile ? "100%" : "85vh",
                overflowY: "auto",
                padding: "16px",
                boxSizing: "border-box",
                fontFamily: "'Segoe UI', sans-serif",
              }}
            >
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: "8px", marginBottom: "14px" }}>
                {topic ? (
                  <button type="button" onClick={() => setTopic(null)} style={navBtn}>
                    {t("help.back")}
                  </button>
                ) : (
                  <h2 style={{ color: cyan, fontSize: "16px", letterSpacing: "2px", margin: 0 }}>{t("help.title")}</h2>
                )}
                <button type="button" onClick={close} aria-label={t("help.close")} style={{ ...navBtn, minWidth: "44px" }}>
                  ✕
                </button>
              </div>

              {topic ? (
                <HelpArticle topic={topic} />
              ) : (
                <>
                  <p style={{ color: "rgba(255,255,255,0.6)", fontSize: "14px", margin: "0 0 12px" }}>{t("help.pick")}</p>
                  {HELP_TOPICS.map((key) => (
                    <button
                      key={key}
                      type="button"
                      onClick={() => setTopic(key)}
                      style={{
                        display: "block",
                        width: "100%",
                        textAlign: "left",
                        minHeight: "56px",
                        padding: "10px 14px",
                        marginBottom: "8px",
                        background: "rgba(0,245,255,0.04)",
                        border: `1px solid ${border}`,
                        borderRadius: "8px",
                        cursor: "pointer",
                      }}
                    >
                      <div style={{ color: "white", fontSize: "15px", fontWeight: "700" }}>{t(`help.topics.${key}.title`)}</div>
                      <div style={{ color: "rgba(255,255,255,0.5)", fontSize: "13px", marginTop: "2px" }}>
                        {t(`help.topics.${key}.summary`)}
                      </div>
                    </button>
                  ))}
                  <p style={{ color: "rgba(255,255,255,0.4)", fontSize: "13px", marginTop: "14px" }}>{t("help.stillStuck")}</p>
                </>
              )}
            </div>
          </div>,
          document.body,
        )}
    </>
  );
}
